import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, LogOut, Menu, Users, HardHat, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { DEPTS } from "@/nav";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { GlobalSearch } from "@/components/GlobalSearch";
import { NotificationBell } from "@/components/NotificationBell";

const topCls = ({ isActive }) =>
  `flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-medium transition-colors ${isActive ? "bg-white text-sky-700 shadow-md" : "text-blue-50/90 hover:bg-white/10 hover:text-white"}`;

const subCls = ({ isActive }) =>
  `flex items-center gap-2 rounded-md px-3 py-1.5 text-xs transition-colors ${isActive ? "bg-white/20 font-semibold text-white" : "text-blue-100/80 hover:bg-white/10 hover:text-white"}`;

const DeptGroup = ({ dept, onNavigate }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const active = pathname.startsWith(`/${dept.id}/`);
  const [open, setOpen] = useState(active);
  useEffect(() => {
    setOpen(active);
  }, [active]);
  const Icon = dept.icon;
  const toggle = () => {
    if (!active) {
      setOpen(true);
      navigate(`/${dept.id}/dashboard`);
      return;
    }
    setOpen((o) => !o);
  };
  const shown = open;
  return (
    <div>
      <button onClick={toggle} data-testid={`nav-${dept.id}`}
        className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-medium transition-colors ${active ? "bg-white/15 text-white" : "text-blue-50/90 hover:bg-white/10 hover:text-white"}`}>
        <Icon className="h-4 w-4" />
        <span className="flex-1 text-left">{dept.name}</span>
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${shown ? "" : "-rotate-90"}`} />
      </button>
      {shown && (
        <div className="ml-5 mt-0.5 space-y-0.5 border-l border-white/15 pl-2" data-testid={`dept-panel-${dept.id}`}>
          {dept.items.map((it) => (
            <NavLink key={it.slug} to={`/${dept.id}/${it.slug}`} className={subCls} onClick={onNavigate} data-testid={`subnav-${dept.id}-${it.slug}`}>
              <span className="flex-1">{it.label}</span>
              {it.flagship && <span className="h-1.5 w-1.5 rounded-full bg-amber-300" title="Key workflow" />}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
};

const Sidebar = ({ onNavigate }) => {
  const { user } = useAuth();
  return (
    <div className="flex h-full w-60 flex-col bg-gradient-to-b from-[#1E64B4] via-[#17549C] to-[#0B3D7A] text-white">
      <div className="flex items-center gap-2.5 px-4 py-4">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/25"><HardHat className="h-5 w-5" /></div>
        <div>
          <p className="font-heading text-lg font-bold leading-none">P2P Ops</p>
          <p className="mt-1 text-[10px] uppercase tracking-wider text-blue-100/70">Procure-to-Pay Suite</p>
        </div>
      </div>
      <nav className="rail-scroll flex-1 space-y-1 overflow-y-auto px-3 py-2">
        <NavLink to="/" end className={topCls} onClick={onNavigate} data-testid="nav-dashboard"><LayoutDashboard className="h-4 w-4" />Dashboard</NavLink>
        <p className="px-3 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-blue-100/60">Departments</p>
        {DEPTS.map((d) => <DeptGroup key={d.id} dept={d} onNavigate={onNavigate} />)}
        {user?.role === "admin" && (
          <>
            <p className="px-3 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-blue-100/60">Administration</p>
            <NavLink to="/users" className={topCls} onClick={onNavigate} data-testid="nav-users"><Users className="h-4 w-4" />Users & Roles</NavLink>
          </>
        )}
      </nav>
      <div className="flex items-center gap-2.5 border-t border-white/10 px-4 py-3" data-testid="sidebar-user">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-xs font-bold text-sky-700">{(user?.name || "?").slice(0, 1)}</div>
        <div className="min-w-0">
          <p className="truncate text-xs font-semibold">{user?.name}</p>
          <p className="truncate text-[10px] text-blue-100/70">{user?.email}</p>
        </div>
      </div>
    </div>
  );
};

const TopBar = ({ onMenu }) => {
  const { user, logout } = useAuth();
  const initials = (user?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-slate-200/80 bg-white/95 px-3 backdrop-blur sm:px-5">
      <Button size="icon" variant="ghost" className="lg:hidden" onClick={onMenu} data-testid="btn-mobile-menu"><Menu className="h-5 w-5" /></Button>
      <div className="min-w-0 flex-1"><GlobalSearch /></div>
      <div className="ml-auto flex items-center gap-3">
        <NotificationBell />
        <div className="hidden text-right sm:block">
          <p className="text-xs font-semibold text-slate-900" data-testid="current-user-name">{user?.name}</p>
          <p className="mt-0.5 inline-block rounded-full bg-sky-50 px-2 py-0.5 text-[10px] font-medium text-sky-700" data-testid="current-user-role">{user?.role_label}</p>
        </div>
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-sky-600 text-xs font-bold text-white">{initials}</div>
        <Button size="icon" variant="outline" className="h-8 w-8 rounded-full" onClick={logout} data-testid="btn-logout" title="Logout" aria-label="Logout"><LogOut className="h-3.5 w-3.5" /></Button>
      </div>
    </header>
  );
};

export default function AppLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  return (
    <div className="flex min-h-screen bg-background">
      <aside className="sticky top-0 hidden h-screen shrink-0 lg:block"><Sidebar /></aside>
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-60 border-0 p-0 [&>button]:text-white [&>button]:opacity-90"><Sidebar onNavigate={() => setMobileOpen(false)} /></SheetContent>
      </Sheet>
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar onMenu={() => setMobileOpen(true)} />
        <main className="flex-1 p-3 sm:p-4 lg:p-5"><Outlet /></main>
      </div>
    </div>
  );
}
